import { isThisWeek, isToday, isYesterday } from "date-fns";

import { type NotificationDto } from "@/features/notifications/model/notifications.types";

/** Recency buckets for the notification center, rendered in this order. */
export enum NotificationTimeBucket {
  Today = "today",
  Yesterday = "yesterday",
  ThisWeek = "thisWeek",
  Earlier = "earlier",
}

const BUCKET_ORDER: NotificationTimeBucket[] = [
  NotificationTimeBucket.Today,
  NotificationTimeBucket.Yesterday,
  NotificationTimeBucket.ThisWeek,
  NotificationTimeBucket.Earlier,
];

function resolveBucket(date: Date): NotificationTimeBucket {
  if (isToday(date)) return NotificationTimeBucket.Today;
  if (isYesterday(date)) return NotificationTimeBucket.Yesterday;
  // weekStartsOn: 1 (Monday) keeps "this week" aligned with the parish work week.
  if (isThisWeek(date, { weekStartsOn: 1 })) return NotificationTimeBucket.ThisWeek;
  return NotificationTimeBucket.Earlier;
}

export type NotificationTimeGroup = {
  bucket: NotificationTimeBucket;
  items: NotificationDto[];
};

/**
 * Groups notifications into ordered recency buckets, preserving the incoming order within each
 * bucket (the API already returns newest-first). Empty buckets are omitted.
 */
export function groupNotificationsByTime(items: NotificationDto[]): NotificationTimeGroup[] {
  const grouped = new Map<NotificationTimeBucket, NotificationDto[]>();

  for (const notification of items) {
    const bucket = resolveBucket(new Date(notification.createdAt));
    const existing = grouped.get(bucket);
    if (existing) {
      existing.push(notification);
    } else {
      grouped.set(bucket, [notification]);
    }
  }

  return BUCKET_ORDER.filter((bucket) => grouped.has(bucket)).map((bucket) => ({
    bucket,
    items: grouped.get(bucket) ?? [],
  }));
}
